import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { api, errMsg } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Search, MapPin, X } from "lucide-react";

function PropertyStep({ prop, onPick, onClear }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (prop) return;
    const t = setTimeout(() => {
      api.get("/properties/available", { params: { q } }).then(({ data }) => setResults(data));
    }, 250);
    return () => clearTimeout(t);
  }, [q, prop]);

  return (
    <div className="mb-8">
      <Label className="font-semibold text-[#1a1f36]">1 · Your property</Label>
      {!prop ? (
        <div className="relative mt-2">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <Input className="pl-9" placeholder="Search by name or city…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="property-search-input" />
          <div className="mt-2 border border-slate-200 rounded-md divide-y max-h-72 overflow-auto" data-testid="property-search-results">
            {results.map((p) => (
              <button key={p.id} type="button" onClick={() => onPick(p)} className="w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-colors" data-testid={`property-option-${p.id}`}>
                <div className="font-medium text-[#1a1f36]">{p.name}</div>
                <div className="text-xs text-slate-500 flex items-center gap-2">
                  <MapPin className="w-3 h-3" />{p.location} · {p.division}
                  {p.division === "Support" ? " · counts everyone who joins" : p.tier ? ` · ${p.tier} (${p.capacity} ${p.capacity_type})` : " · capacity needed"}
                </div>
              </button>
            ))}
            {results.length === 0 && <div className="px-4 py-6 text-sm text-slate-500 text-center">No properties match "{q}". Contact your admin if yours is missing.</div>}
          </div>
        </div>
      ) : (
        <div className="mt-2 border-2 border-[#1a1f36] rounded-md px-4 py-3 flex items-start justify-between" data-testid="selected-property">
          <div>
            <div className="font-semibold text-[#1a1f36]">{prop.name}</div>
            <div className="text-xs text-slate-500">{prop.location} · {prop.division === "Support" ? "Support Team" : `${prop.division} Housing`}{prop.tier && prop.division !== "Support" ? ` · ${prop.tier} tier` : ""}</div>
            {prop.note && <div className="text-xs text-slate-500 mt-1">Hint: {prop.note}</div>}
          </div>
          <button type="button" onClick={onClear} className="text-slate-400 hover:text-slate-700" data-testid="clear-property-btn"><X className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
}

function ClaimDetails({ prop, form, set, setForm, regionals, needsCapacity, isSupport }) {
  return (
    <div>
      <Label className="font-semibold text-[#1a1f36]">2 · Confirm the details</Label>
      {isSupport ? (
        <p className="text-sm text-slate-600 border border-slate-200 bg-[#f8f9fc] rounded-md p-3 mt-2" data-testid="support-claim-note">
          No capacity or Regional needed — your floor's size is counted automatically from everyone who signs up. Just create your account below.
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4 mt-2">
          <div>
            <Label htmlFor="cap" className="text-sm text-slate-600">Capacity ({prop.capacity_type}) {needsCapacity && <span className="text-[#c8102e]">*required</span>}</Label>
            <Input id="cap" type="number" min="1" value={form.capacity} onChange={set("capacity")} required={needsCapacity} disabled={!!prop.capacity} data-testid="capacity-input" />
            {prop.capacity ? <p className="text-xs text-slate-400 mt-1">Already on file — ask an admin if it's wrong.</p> : null}
          </div>
          <div>
            <Label className="text-sm text-slate-600">Regional / Commander {!prop.regional && <span className="text-[#c8102e]">*required</span>}</Label>
            <Select value={form.regional} onValueChange={(v) => setForm((f) => ({ ...f, regional: v }))} disabled={!!prop.regional} required>
              <SelectTrigger data-testid="regional-select"><SelectValue placeholder="Select your Regional" /></SelectTrigger>
              <SelectContent>
                {regionals.map((r) => <SelectItem key={r} value={r} data-testid={`regional-option-${r}`}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="contact" className="text-sm text-slate-600">Contact name</Label>
            <Input id="contact" value={form.contact_name} onChange={set("contact_name")} data-testid="contact-name-input" />
          </div>
        </div>
      )}
    </div>
  );
}

function AccountFields({ form, set }) {
  return (
    <div>
      <Label className="font-semibold text-[#1a1f36]">3 · Your account (property lead)</Label>
      <div className="grid sm:grid-cols-2 gap-4 mt-2">
        <div>
          <Label htmlFor="name" className="text-sm text-slate-600">Your name</Label>
          <Input id="name" value={form.name} onChange={set("name")} required data-testid="register-name" />
        </div>
        <div>
          <Label htmlFor="email" className="text-sm text-slate-600">Email</Label>
          <Input id="email" type="email" value={form.email} onChange={set("email")} required placeholder="you@redstoneresidential.com" data-testid="register-email" />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="pw" className="text-sm text-slate-600">Password (min 8 characters)</Label>
          <Input id="pw" type="password" value={form.password} onChange={set("password")} required minLength={8} data-testid="register-password" />
        </div>
      </div>
    </div>
  );
}

export default function Register() {
  const { signIn, user } = useAuth();
  const nav = useNavigate();
  const [prop, setProp] = useState(null);
  const [regionals, setRegionals] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", capacity: "", regional: "", contact_name: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user?.role === "property_user") { nav("/dashboard"); return; }
    api.get("/regionals").then(({ data }) => setRegionals(data));
  }, [user, nav]);

  const pick = (p) => {
    setProp(p);
    setForm((f) => ({
      ...f,
      capacity: p.capacity ?? "",
      regional: p.regional || "",
      contact_name: p.community_manager || "",
    }));
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!prop) return toast.error("Select your property first");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register-claim", {
        name: form.name, email: form.email, password: form.password,
        property_id: prop.id, capacity: form.capacity ? parseInt(form.capacity, 10) : null,
        regional: form.regional, contact_name: form.contact_name,
      });
      signIn(data);
      toast.success(`You're on the ${prop.name} squad — welcome to Site Wars!`);
      nav("/dashboard");
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };

  const needsCapacity = prop && prop.division !== "Support" && (prop.status === "Needs capacity" || !prop.capacity);
  const isSupport = prop?.division === "Support";

  return (
    <div className="max-w-2xl mx-auto px-4 py-10 fade-up">
      <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#c8102e] mb-2" data-testid="claim-open-note">Sign-up is open the whole competition</p>
      <h1 className="font-display text-4xl font-bold text-[#1a1f36] mb-1">Join your property</h1>
      <p className="text-slate-600 mb-8">Every property is already competing — signing up just links you to your team so you can log tasks, save your reel, and track your standing. Teammates can all join the same property. Use your redstoneresidential.com email.</p>

      {/* Step 1: pick property */}
      <PropertyStep prop={prop} onPick={pick} onClear={() => setProp(null)} />

      {prop && (
        <form onSubmit={submit} className="space-y-8">
          <ClaimDetails prop={prop} form={form} set={set} setForm={setForm} regionals={regionals} needsCapacity={needsCapacity} isSupport={isSupport} />
          <AccountFields form={form} set={set} />

          <Button type="submit" disabled={busy} className="w-full sw-red hover:bg-red-700 text-white font-semibold h-11" data-testid="register-submit">
            {busy ? "Joining…" : `Join ${prop.name}`}
          </Button>
        </form>
      )}
    </div>
  );
}
